import { Link } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useSEO, useStructuredData, generateFAQStructuredData, mergeKeywordSets, COMPETITIVE_KEYWORD_CLUSTERS } from '../lib/seo';
import {
  ArrowRight,
  Check,
  Flame,
  Heart,
  MessageCircle,
  Minus,
  Play,
  Shield,
  Sparkles,
  Zap,
} from 'lucide-react';

/** Illustrative scores for visual scorecard only — not empirical benchmark data. */
const scorecardBars: { label: string; tension: number; paired: number }[] = [
  { label: 'De-escalation & repair focus', tension: 94, paired: 72 },
  { label: 'Tension awareness', tension: 96, paired: 58 },
  { label: 'Message-level coaching', tension: 92, paired: 55 },
  { label: 'Daily prompts & quizzes', tension: 62, paired: 95 },
  { label: 'Content breadth', tension: 68, paired: 92 },
];

const faqs = [
  {
    question: 'Is TENSION the same as Paired?',
    answer:
      'No. Both support couples communication, but TENSION focuses on tension forecasting, repair workflows, and actionable daily guidance.',
  },
  {
    question: 'Who should choose TENSION?',
    answer:
      'Couples who want practical conflict prevention and message-level communication coaching in one workflow.',
  },
  {
    question: 'Can I compare before subscribing?',
    answer:
      'Yes. Review pricing and feature pages first, then choose the plan that matches your goals.',
  },
];

type Row = {
  label: string;
  sub?: string;
  tension: 'full' | 'partial' | 'none';
  paired: 'full' | 'partial' | 'none';
};

const matrixRows: Row[] = [
  {
    label: 'Tension & pattern awareness',
    sub: 'See where stress builds before it blows up',
    tension: 'full',
    paired: 'partial',
  },
  {
    label: 'Repair-first workflows',
    sub: 'Steps aimed at de-escalation and follow-through',
    tension: 'full',
    paired: 'partial',
  },
  {
    label: 'Message-level coaching',
    sub: 'Micro-coach for texts and tricky replies',
    tension: 'full',
    paired: 'partial',
  },
  {
    label: 'Daily prompts & quizzes',
    sub: 'Lightweight check-ins and playful engagement',
    tension: 'partial',
    paired: 'full',
  },
  {
    label: 'Broad couples content library',
    sub: 'Wide variety of exercises and themes',
    tension: 'partial',
    paired: 'full',
  },
];

function CellIcon({ level }: { level: 'full' | 'partial' | 'none' }) {
  if (level === 'full') {
    return (
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 ring-2 ring-emerald-400/40 shadow-[0_0_20px_rgba(16,185,129,0.35)]">
        <Check className="h-5 w-5 stroke-[2.5]" aria-hidden />
      </span>
    );
  }
  if (level === 'partial') {
    return (
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/15 text-amber-700 ring-2 ring-amber-400/35">
        <Minus className="h-5 w-5 stroke-[2.5]" aria-hidden />
      </span>
    );
  }
  return (
    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-neutral-200/80 text-neutral-500 ring-1 ring-neutral-300/80">
      <Minus className="h-5 w-5 opacity-60" aria-hidden />
    </span>
  );
}

export default function CompareTensionVsPaired() {
  useSEO({
    title: 'TENSION vs Paired: Relationship App Comparison',
    description:
      'Compare TENSION vs Paired across communication coaching, conflict prevention workflow, and pricing fit.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.alternatives as unknown as string[],
      ['tension vs paired', 'paired comparison', 'relationship app comparison'],
    ),
    canonical: '/compare/tension-vs-paired',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  useStructuredData(generateFAQStructuredData(faqs));

  return (
    <div className="neon-bg relative min-h-screen overflow-hidden">
      {/* Background POP: mesh + grid */}
      <div
        className="neon-mesh pointer-events-none absolute inset-0"
        aria-hidden
      />
      <div
        className="neon-grid pointer-events-none absolute inset-0 opacity-40"
        aria-hidden
      />

      <div className="relative mx-auto max-w-6xl px-4 py-10 md:py-14">
        {/* Hero: split duel layout */}
        <header className="neon-glass relative overflow-hidden rounded-3xl">
          <div className="grid gap-0 md:grid-cols-[1fr_auto_1fr]">
            {/* TENSION column */}
            <div className="relative bg-gradient-to-br from-violet-600/90 via-violet-700/90 to-fuchsia-700/80 p-8 md:p-10">
              <div className="absolute right-4 top-4 opacity-20">
                <Sparkles className="h-24 w-24 text-white" aria-hidden />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-100/90">Your edge</p>
              <h1 className="mt-2 text-4xl font-black tracking-tight text-white drop-shadow-sm md:text-5xl">
                TENSION
              </h1>
              <p className="mt-3 max-w-sm text-sm font-medium leading-relaxed text-violet-100/95">
                Built for couples who want fewer blow-ups: tension signals, repair workflows, and message-level coaching
                in one loop.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/25">
                  <Zap className="h-3.5 w-3.5" /> Prevention-first
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/25">
                  <MessageCircle className="h-3.5 w-3.5" /> Micro-coach
                </span>
              </div>
            </div>

            {/* VS badge */}
            <div className="flex items-center justify-center bg-neutral-950/80 py-6 md:py-0 md:px-2">
              <div className="flex h-20 w-20 rotate-3 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-lg font-black text-neutral-950 shadow-lg shadow-orange-500/40 ring-4 ring-amber-400/30 md:h-24 md:w-24 md:text-xl">
                VS
              </div>
            </div>

            {/* Paired column (text-only; no third-party marks) */}
            <div className="relative border-t border-white/10 bg-gradient-to-br from-neutral-800/95 to-neutral-900/95 p-8 md:border-t-0 md:border-l md:border-white/10 md:p-10">
              <div className="absolute left-4 top-4 opacity-15">
                <Heart className="h-20 w-20 text-pink-300" aria-hidden />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">Household name</p>
              <h2 className="mt-2 text-4xl font-black tracking-tight text-white md:text-5xl">Paired</h2>
              <p className="mt-3 max-w-sm text-sm font-medium leading-relaxed text-neutral-300">
                Known for daily prompts, quizzes, and a broad content-first experience for couples who like structured
                check-ins.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-neutral-100 ring-1 ring-white/15">
                  Daily prompts
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-neutral-100 ring-1 ring-white/15">
                  Quizzes & games
                </span>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 bg-black/20 px-6 py-4 text-center text-xs text-zinc-300 md:px-10">
            Independent comparison for shoppers — not affiliated with Paired. Features change; confirm on each app before
            you subscribe.
          </div>
        </header>

        {/* Illustrative scorecard + device mockups */}
        <section className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-stretch">
          <Card className="relative overflow-hidden border-fuchsia-500/25 bg-gradient-to-br from-fuchsia-950/80 via-violet-950/60 to-neutral-950 p-6 shadow-[0_0_60px_-10px_rgba(217,70,239,0.35)] md:p-8">
            <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-fuchsia-500/20 blur-3xl" aria-hidden />
            <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-fuchsia-100 ring-1 ring-white/15">
              <Sparkles className="h-3.5 w-3.5" /> At-a-glance scorecard
            </p>
            <h2 className="mt-3 text-2xl font-black tracking-tight text-white md:text-3xl">
              Where energy goes (illustrative)
            </h2>
            <p className="mt-2 text-xs text-zinc-300">
              Visual shorthand — not third-party benchmark data. Swap numbers anytime as positioning evolves.
            </p>
            <div className="mt-6 space-y-5">
              {scorecardBars.map((row) => (
                <div key={row.label}>
                  <div className="mb-1.5 flex justify-between text-xs font-medium text-neutral-300">
                    <span>{row.label}</span>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1 space-y-1">
                      <div className="flex justify-between text-[10px] uppercase tracking-wide text-violet-300/90">
                        <span>TENSION</span>
                        <span className="font-mono text-violet-100">{row.tension}</span>
                      </div>
                      <div className="h-3 overflow-hidden rounded-full bg-black/40 ring-1 ring-violet-400/30">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 shadow-[0_0_12px_rgba(139,92,246,0.7)] transition-all duration-700 ease-out"
                          style={{ width: `${row.tension}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex justify-between text-[10px] uppercase tracking-wide text-neutral-500">
                        <span>Paired</span>
                        <span className="font-mono text-neutral-300">{row.paired}</span>
                      </div>
                      <div className="h-3 overflow-hidden rounded-full bg-black/40 ring-1 ring-white/10">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-neutral-500 to-pink-500/80 shadow-inner transition-all duration-700 ease-out"
                          style={{ width: `${row.paired}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <div className="flex flex-col gap-4">
            <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-zinc-400">
              In the hand (illustrative UI)
            </p>
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <div className="group relative mx-auto w-full max-w-[200px]">
                <div className="absolute -inset-1 rounded-[2rem] bg-gradient-to-br from-violet-500 via-fuchsia-500 to-amber-400 opacity-70 blur-md transition group-hover:opacity-100" />
                <div className="relative aspect-[9/18] w-full overflow-hidden rounded-[1.75rem] border-4 border-white/15 bg-neutral-900 shadow-2xl">
                  <div className="absolute inset-0 animate-pulse bg-gradient-to-b from-violet-600/50 via-fuchsia-900/40 to-neutral-950 motion-reduce:animate-none" />
                  <div className="absolute inset-3 rounded-2xl bg-[linear-gradient(180deg,rgba(255,255,255,0.12)_0%,transparent_40%)]" />
                  <div className="absolute bottom-4 left-2 right-2 rounded-lg bg-black/50 px-2 py-1.5 text-center text-[10px] font-semibold text-white backdrop-blur-sm">
                    TENSION — RI / micro-coach
                  </div>
                </div>
              </div>
              <div className="group relative mx-auto w-full max-w-[200px]">
                <div className="absolute -inset-1 rounded-[2rem] bg-gradient-to-br from-neutral-600 via-pink-900/50 to-neutral-800 opacity-60 blur-md transition group-hover:opacity-90" />
                <div className="relative aspect-[9/18] w-full overflow-hidden rounded-[1.75rem] border-4 border-white/10 bg-neutral-900 shadow-2xl">
                  <img
                    src="/images/compare/paired-home-daily-activities.png"
                    alt="Paired app Home screen: Daily Activities with question, quiz, and game cards"
                    className="h-full w-full object-cover object-top"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 to-transparent" aria-hidden />
                  <div className="absolute bottom-4 left-2 right-2 rounded-lg bg-black/55 px-2 py-1.5 text-center text-[10px] font-semibold text-neutral-100 backdrop-blur-sm">
                    Paired — Home (daily activities)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Cinematic CTA strip — feels like a hero video */}
        <Link
          to="/features/relationship-intelligence"
          className="mt-8 block overflow-hidden rounded-3xl border border-white/10 bg-neutral-900/80 shadow-2xl ring-1 ring-violet-500/20 transition hover:ring-violet-400/40"
        >
          <div className="relative aspect-[21/9] min-h-[140px] md:min-h-[200px]">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_100%_at_50%_100%,rgba(124,58,237,0.45),transparent),linear-gradient(135deg,rgba(15,15,25,0.9),rgba(50,20,80,0.85))]" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2240%22%20height%3D%2240%22%3E%3Cpath%20d%3D%22M0%200h40v40H0z%22%20fill%3D%22none%22%2F%3E%3Cpath%20d%3D%22M40%200v40M0%2040h40%22%20stroke%3D%22rgba(255%2C255%2C255%2C0.04)%22%20stroke-width%3D%221%22%2F%3E%3C%2Fsvg%3E')] opacity-60" />
            <div className="relative flex h-full flex-col items-center justify-center gap-3 px-6 text-center md:flex-row md:gap-8">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white/15 text-white shadow-lg ring-4 ring-violet-400/30 md:h-20 md:w-20">
                <Play className="ml-1 h-8 w-8 fill-current md:h-10 md:w-10" aria-hidden />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-200/90">Experience-first</p>
                <p className="mt-1 text-xl font-black text-white md:text-2xl">Explore Relationship Intelligence</p>
                <p className="mt-1 max-w-xl text-sm text-neutral-300">
                  Tap through the workflow TENSION is built around — then stack-rank against your Paired trial.
                </p>
              </div>
            </div>
          </div>
        </Link>

        {/* Visual matrix */}
        <section className="mt-10 md:mt-14">
          <div className="mb-6 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-fuchsia-500/20 px-3 py-1 text-xs font-bold uppercase tracking-wider text-fuchsia-200 ring-1 ring-fuchsia-400/30">
                <Flame className="h-3.5 w-3.5" /> Side-by-side snapshot
              </p>
              <h2 className="mt-3 text-3xl font-black tracking-tight text-white md:text-4xl">
                Where each app shines
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-zinc-300">
                Full = strong fit · Partial = some overlap · Limited = not the main focus (how we see the category —
                your mileage may vary).
              </p>
            </div>
            <div className="flex gap-3 text-xs text-zinc-300">
              <span className="inline-flex items-center gap-1.5">
                <CellIcon level="full" /> Strong
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CellIcon level="partial" /> Partial
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CellIcon level="none" /> Limited
              </span>
            </div>
          </div>

          <Card className="overflow-x-auto border-white/10 bg-white/[0.04] shadow-2xl shadow-violet-900/20 backdrop-blur-md">
            <table className="w-full min-w-[320px] border-collapse">
              <colgroup>
                <col />
                <col className="w-[6rem]" />
                <col className="w-[6rem]" />
              </colgroup>
              <thead>
                <tr className="border-b border-white/10 bg-white/5 text-[10px] font-bold uppercase tracking-wider text-zinc-300 sm:text-xs md:text-sm">
                  <th className="p-3 text-left sm:p-4 md:p-5">Capability</th>
                  <th className="border-l border-white/10 bg-violet-500/10 p-2 text-center text-violet-100 sm:p-3 md:p-5">
                    TENSION
                  </th>
                  <th className="border-l border-white/10 p-2 text-center text-neutral-200 sm:p-3 md:p-5">Paired</th>
                </tr>
              </thead>
              <tbody>
                {matrixRows.map((row) => (
                  <tr key={row.label} className="border-b border-white/5 last:border-0">
                    <td className="min-w-0 p-3 align-middle sm:p-4 md:p-5">
                      <p className="font-semibold text-white">{row.label}</p>
                      {row.sub ? <p className="mt-1 text-sm text-zinc-400">{row.sub}</p> : null}
                    </td>
                    <td className="border-l border-white/5 bg-violet-500/[0.03] p-2 text-center align-middle sm:p-4 md:p-5">
                      <span className="inline-flex items-center justify-center">
                        <CellIcon level={row.tension} />
                      </span>
                    </td>
                    <td className="border-l border-white/5 p-2 text-center align-middle sm:p-4 md:p-5">
                      <span className="inline-flex items-center justify-center">
                        <CellIcon level={row.paired} />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </section>

        {/* Punchy CTAs */}
        <div className="mt-10 grid gap-4 md:grid-cols-2 md:gap-6">
          <Card className="relative overflow-hidden border-violet-400/30 bg-gradient-to-br from-violet-600/40 to-fuchsia-600/30 p-6 shadow-lg shadow-violet-900/30 md:p-8">
            <Shield className="absolute -right-4 -top-4 h-32 w-32 text-white/10" aria-hidden />
            <h3 className="text-xl font-bold text-white md:text-2xl">Lean into calmer communication</h3>
            <p className="mt-2 text-sm text-violet-100/90">
              If your priority is fewer escalation loops and clearer next steps, start with Relationship Intelligence and
              pricing.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/pricing">
                <Button className="bg-white text-violet-900 hover:bg-violet-50">
                  View pricing <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/features/relationship-intelligence">
                <Button variant="outline" className="border-white/40 bg-white/10 text-white hover:bg-white/20">
                  Explore RI
                </Button>
              </Link>
            </div>
          </Card>

          <Card className="border-white/10 bg-white/[0.06] p-6 backdrop-blur md:p-8">
            <h3 className="text-xl font-bold text-white md:text-2xl">Still exploring?</h3>
            <p className="mt-2 text-sm text-zinc-300">
              See more options in the same keyword cluster and keep your shortlist honest.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/alternatives/paired">
                <Button variant="secondary" className="bg-neutral-100 text-neutral-900 hover:bg-white">
                  Paired alternatives
                </Button>
              </Link>
              <Link to="/best-couples-app">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  Best couples apps
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
